// api/PlanManager.js
// Loads a room plan from S3:
//   s3://azrius-data/geovision/{COGNITO_ID}/living_harmony_profile/project/{PROJECT_ID}/room/{ROOM_ID}/plan.json
//
// Usage:
//   import PlanManager from "./api/PlanManager";
//   const plan = await PlanManager.loadPlan({ cognitoId, projectId, roomId });
//   // if file doesn't exist -> {}

import AWS from "aws-sdk";

const BUCKET = "azrius-data";

/** Build the S3 key for plan.json */
export function planKey({ cognitoId, projectId, roomId }) {
  if (!cognitoId || !projectId || !roomId) {
    throw new Error(
      "[PlanManager] planKey requires cognitoId, projectId, and roomId",
    );
  }
  return `geovision/${cognitoId}/living_harmony_profile/project/${projectId}/room/${roomId}/plan.json`;
}

/** Robustly convert S3 Body -> string across Node/browser */
async function bodyToString(body) {
  if (body == null) return "";
  // String
  if (typeof body === "string") return body;
  // Browser Blob
  if (typeof Blob !== "undefined" && body instanceof Blob) {
    return await body.text();
  }
  // Node Buffer
  if (
    typeof Buffer !== "undefined" &&
    Buffer.isBuffer &&
    Buffer.isBuffer(body)
  ) {
    return body.toString("utf-8");
  }
  // Uint8Array / ArrayBuffer
  const decoder = new TextDecoder("utf-8");
  if (body instanceof ArrayBuffer) return decoder.decode(new Uint8Array(body));
  if (body instanceof Uint8Array) return decoder.decode(body);

  // Fallback
  try {
    return String(body);
  } catch {
    return "";
  }
}

/**
 * Load plan.json from S3. Returns {} if the object is missing.
 * @param {Object} params
 * @param {string} params.cognitoId
 * @param {string} params.projectId
 * @param {string} params.roomId
 * @param {AWS.S3} [params.s3] Optional injected S3 client (uses AWS config by default)
 * @param {string} [params.bucket="azrius-data"] Override bucket if needed
 */
export async function loadPlan({
  cognitoId,
  projectId,
  roomId,
  s3,
  bucket = BUCKET,
}) {
  const Key = planKey({ cognitoId, projectId, roomId });
  const s3Client = s3 || new AWS.S3();

  try {
    const res = await s3Client
      .getObject({
        Bucket: bucket,
        Key,
      })
      .promise();

    const jsonStr = await bodyToString(res.Body);
    if (!jsonStr.trim()) return {};
    try {
      return JSON.parse(jsonStr);
    } catch (parseErr) {
      console.error("[PlanManager] Failed to parse plan.json:", parseErr);
      // If corrupt JSON, surface the error so we can fix upstream.
      throw parseErr;
    }
  } catch (err) {
    const code = err?.code || err?.name;
    const status = err?.statusCode;
    // Treat missing object as empty plan
    if (code === "NoSuchKey" || code === "NotFound" || status === 404) {
      return {};
    }
    console.error("[PlanManager] S3 getObject error:", err);
    throw err;
  }
}

const PlanManager = { loadPlan, planKey, BUCKET };
export default PlanManager;
